import {SiteLink as RouterLink} from "nav/SiteLink";
import {useLocale} from "i18n";
import {ContentSurface, Prose} from "components/ContentPage";
import {contactCopy} from "./copy/contact";

/**
 * Public, crawlable **Contact & Support** page body (`/contact`, issue ui#133).
 *
 * v1 is email-only (no contact form — deferred to a follow-up issue). It doubles as a credibility
 * signal for Google Ads, so the copy is warm and human: a real support inbox, a soft response-time
 * promise, and a nudge to the FAQ/pricing for readers who can self-serve faster.
 *
 * Dumb view: every string comes from {@link ./copy/contact.ts} (both locales), selected at runtime by
 * `useLocale()` — the same locale mechanism the rest of the site uses, so the `/ro` SSG tree renders
 * Romanian for free. The component only lays the copy out and wires the two links — a `mailto:` for
 * the support address and unprefixed `react-router` links to the internal FAQ/pricing anchors (the
 * `/ro` tree keeps clean paths, per `routes.tsx`). SSG-safe: token-styled markup via `<Prose>`, no
 * browser APIs, no portals, no hardcoded colors.
 */
export function ContactContent() {
	const {locale} = useLocale();
	const copy = contactCopy[locale];
	return (
		<ContentSurface>
			<Prose>
				<h1>{copy.heading}</h1>
				<ContactBody/>
			</Prose>
		</ContentSurface>
	);
}

/** Route-neutral contact copy shared by the in-flow page and the Resources dialog. */
export function ContactBody() {
	const {locale} = useLocale();
	const copy = contactCopy[locale];
	return (
		<>
				<p>{copy.lede}</p>

				<h2>{copy.emailHeading}</h2>
				<p>
					{copy.emailLine.before}
					<a href={`mailto:${copy.email}`}>{copy.email}</a>
					{copy.emailLine.after}
				</p>
				<p>{copy.responseTime}</p>

				<h2>{copy.fasterHeading}</h2>
				<p>{copy.fasterIntro}</p>
				<ul>
					<li>
						<RouterLink to="/features#features-faq-heading">{copy.faqLink.label}</RouterLink>
						{copy.faqLink.description}
					</li>
					<li>
						<RouterLink to="/pricing">{copy.pricingLink.label}</RouterLink>
						{copy.pricingLink.description}
					</li>
				</ul>
				<p>{copy.closing}</p>
		</>
	);
}
